// ./src/linkTargets.ts — WHERE a link button goes (ROADMAP A2e #85).
//
// A Link Buttons block used to hold free-text URLs only. A button now has a KIND:
//   • `url`    — a web address, as before (an absent kind is a url, so every saved block reads unchanged);
//   • `page`   — another page of the event site, stored by page ID (a renamed page keeps working);
//   • `block`  — a scroll to another block on the same page, stored by block ID;
//   • `action` — the event's own actions: register (RSVP or Get tickets — whatever the event's hero offers),
//                my orders / my RSVP, add to calendar.
//
// ⚠⚠ THE SITE DECIDES WHAT EXISTS, NOT THE STORED LINK. A page can be deleted or drafted, a block removed, an RSVP
// closed or the event cancelled — and the hero's buttons already follow all of that. So on the live site a button
// whose target is gone or closed is NOT RENDERED (no dead "#", no form that refuses at the end); in the editor's
// preview every configured button shows, so the host can see what they built.
// ⓘ Pure, so the guard runs it.

/** What a button links to. Absent = `url` (every block saved before #85). */
export type WidgetLinkKind = 'url' | 'page' | 'block' | 'action';

/** The event actions a button can run. */
export type WidgetLinkAction = 'register' | 'mine' | 'calendar';

export const WIDGET_LINK_KINDS: { value: WidgetLinkKind; label: string }[] = [
  { value: 'url', label: 'Web address' },
  { value: 'page', label: 'A page of this site' },
  { value: 'block', label: 'A block on this page' },
  { value: 'action', label: 'An event action' },
];

export const WIDGET_LINK_ACTIONS: { value: WidgetLinkAction; label: string }[] = [
  { value: 'register', label: 'Register / Get tickets (as the event offers)' },
  { value: 'mine', label: 'My orders / My RSVP' },
  { value: 'calendar', label: 'Add to calendar' },
];

/** The DOM id a block's wrapper carries on the live site — what a `block` link scrolls to (`#block-<id>`). */
export const blockAnchorId = (blockId: string): string => `block-${blockId}`;

/** What the live site knows about its own pages, blocks and actions (events-web fills it; the editor does not). */
export interface SiteLinkContext {
  /** The site's VISIBLE pages, with their hrefs. */
  pages: { id: string; href: string }[];
  /** The blocks on THIS page (the ones a `#block-…` anchor can reach). */
  blockIds: string[];
  /** Which actions the event offers right now: register / mine open a drawer (or a join link); calendar is a link. */
  actions: { register: boolean | { href: string }; mine: boolean; calendarHref: string | null };
  /** Opens an action's drawer. */
  onAction: (action: 'register' | 'mine') => void;
  /** Client-side navigation to a page of the site (a plain click; the href stays for SEO and new tabs). */
  navigate?: (href: string) => void;
}

/**
 * What a click on a resolved button does — pure, so the guard can run it:
 * • `action` — run the event action (an action button is a real <button>; it has no address to follow);
 * • `navigate` — a page of the site on a PLAIN click: client-side navigation (a new-tab / modified click follows the
 *   href instead, so "open in new tab" still works);
 * • `inert` — the editor's preview of an in-site link: there is nowhere for it to go there;
 * • `follow` — everything else: the browser follows the href (web addresses, block anchors, the calendar).
 */
export const linkClickPlan = (
  to: ResolvedLink,
  opts: { live: boolean; inSite: boolean; plainClick: boolean; canNavigate: boolean },
): 'action' | 'navigate' | 'inert' | 'follow' => {
  if (!opts.live) {
    return opts.inSite ? 'inert' : 'follow';
  }

  if (to.action) {
    return 'action';
  }

  if (to.internalPage && opts.canNavigate && opts.plainClick) {
    return 'navigate';
  }

  return 'follow';
};

/** The stored link, as the resolver reads it (a superset of `WidgetLink`). */
export interface LinkLike {
  url?: string | null;
  kind?: WidgetLinkKind | string | null;
  pageId?: string | null;
  blockId?: string | null;
  action?: WidgetLinkAction | string | null;
}

export interface ResolvedLink {
  /** Where the anchor points (also what a new tab / crawler follows). */
  href: string;
  /** An outside address — opens in a new tab, rel nofollow. */
  external: boolean;
  /** An action to run instead of following the href. */
  action?: 'register' | 'mine';
  /** A page of this site — navigated client-side on a plain click. */
  internalPage?: boolean;
}

const isBareLink = (u: string): boolean => /^(mailto:|tel:|\/|#)/i.test(u) || /^https?:\/\//i.test(u);

/** A typed address as an href: mailto / tel / a path / an anchor / http(s) as is; anything else gets https://. */
export const normalizeLinkUrl = (u: string): string => {
  const s = u.trim();

  if (!s) {
    return '#';
  }

  return isBareLink(s) ? s : `https://${s}`;
};

const isExternalHref = (u: string): boolean => /^https?:\/\//i.test(u);

/** The link's kind, with an absent / unknown one read as `url`. */
export const linkKindOf = (link: LinkLike): WidgetLinkKind => {
  const k = link.kind;

  return k === 'page' || k === 'block' || k === 'action' ? k : 'url';
};

/** Does the stored link name a target at all? (What the editor saves; an empty target is an unfinished row.) */
export const linkHasTarget = (link: LinkLike): boolean => {
  switch (linkKindOf(link)) {
    case 'page':
      return !!link.pageId;
    case 'block':
      return !!link.blockId;
    case 'action':
      return WIDGET_LINK_ACTIONS.some((a) => a.value === link.action);
    default:
      return !!link.url?.trim();
  }
};

/**
 * Where a button goes. `site` absent = the editor's preview: every configured button resolves (to "#" when it has no
 * address there). With `site`, null = do not render the button — its page or block is gone, or the action is closed.
 */
export const resolveWidgetLink = (link: LinkLike, site?: SiteLinkContext | null): ResolvedLink | null => {
  const kind = linkKindOf(link);

  if (kind === 'url') {
    const href = normalizeLinkUrl(link.url ?? '');

    return { href, external: isExternalHref(href) };
  }

  if (!site) {
    return linkHasTarget(link) ? { href: kind === 'block' && link.blockId ? `#${blockAnchorId(link.blockId)}` : '#', external: false } : null;
  }

  if (kind === 'page') {
    const page = site.pages.find((p) => p.id === link.pageId);

    return page ? { href: page.href, external: false, internalPage: true } : null;
  }

  if (kind === 'block') {
    return link.blockId && site.blockIds.includes(link.blockId) ? { href: `#${blockAnchorId(link.blockId)}`, external: false } : null;
  }

  if (link.action === 'calendar') {
    return site.actions.calendarHref ? { href: site.actions.calendarHref, external: true } : null;
  }

  if (link.action === 'register') {
    const register = site.actions.register;

    if (typeof register === 'object') {
      // An online event's register IS its join link — what the hero shows instead of a form.
      return { href: register.href, external: true };
    }

    return register ? { href: '#', external: false, action: 'register' } : null;
  }

  if (link.action === 'mine') {
    return site.actions.mine ? { href: '#', external: false, action: 'mine' } : null;
  }

  return null;
};
